import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useBreathAudioCue — soft, optional "in… / out…" whisper tones synced to the
 * BreathingPacer phase changes.
 *
 * Inhale (phase 0):  ascending breath-like tone (220Hz → 280Hz)
 * Hold   (phase 1):  silent
 * Exhale (phase 2):  descending tone           (200Hz → 150Hz)
 * Rest   (phase 3):  silent
 *
 * The tones are intentionally low-gain (~0.06) and bandpass-filtered so they
 * sound like a soft whisper rather than a chime. AudioContext is lazy: it's
 * only created the first time the user enables cues, and resumes on a user
 * gesture (browsers require this for autoplay).
 *
 * API:
 *   enabled         current state
 *   setEnabled(bool)
 *   notify(phaseIdx, phaseDurationSec)   — fires the appropriate tone
 */
export default function useBreathAudioCue() {
  const [enabled, setEnabledState] = useState(false);
  const ctxRef = useRef(null);
  const masterGainRef = useRef(null);
  const enabledRef = useRef(false);
  const activeNodesRef = useRef([]); // currently-playing oscillators we may need to stop

  const ensureCtx = useCallback(async () => {
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      const ctx = new Ctx();
      const master = ctx.createGain();
      master.gain.value = 0.06; // overall ceiling — soft whisper
      master.connect(ctx.destination);
      ctxRef.current = ctx;
      masterGainRef.current = master;
    }
    if (ctxRef.current.state === "suspended") {
      try {
        await ctxRef.current.resume();
      } catch (err) {
        // Browser may refuse to resume without a user gesture. Cue is
        // disabled implicitly — user can re-toggle to retry.
        console.warn("[useBreathAudioCue] resume failed:", err);
      }
    }
    return ctxRef.current;
  }, []);

  const setEnabled = useCallback(async (v) => {
    enabledRef.current = !!v;
    setEnabledState(!!v);
    if (v) {
      await ensureCtx();
    } else {
      // stop any currently-playing tones immediately
      activeNodesRef.current.forEach((n) => {
        try { n.stop(); } catch (err) { console.debug("breath-cue node stop:", err); }
      });
      activeNodesRef.current = [];
    }
  }, [ensureCtx]);

  const notify = useCallback((phaseIdx, durationSec) => {
    if (!enabledRef.current) return;
    const ctx = ctxRef.current;
    const master = masterGainRef.current;
    if (!ctx || !master) return;
    // silent phases (hold / rest)
    if (phaseIdx === 1 || phaseIdx === 3 || !durationSec || durationSec <= 0) return;

    const now = ctx.currentTime;
    const dur = Math.max(0.4, Math.min(12, durationSec));

    // Oscillator + bandpass filter + envelope = "breath whisper"
    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const env = ctx.createGain();

    filter.type = "bandpass";
    filter.frequency.value = 350;
    filter.Q.value = 0.9;

    if (phaseIdx === 0) {
      // inhale — slight upward sweep
      osc.frequency.setValueAtTime(220, now);
      osc.frequency.linearRampToValueAtTime(280, now + dur);
    } else {
      // exhale — downward sweep
      osc.frequency.setValueAtTime(200, now);
      osc.frequency.linearRampToValueAtTime(150, now + dur);
    }
    osc.type = "sine";

    // Envelope: fade in (10% of phase), sustain, fade out (25% of phase)
    const fadeIn = Math.min(0.6, dur * 0.1);
    const fadeOut = Math.min(1.0, dur * 0.25);
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(1, now + fadeIn);
    env.gain.setValueAtTime(1, now + Math.max(fadeIn, dur - fadeOut));
    env.gain.linearRampToValueAtTime(0, now + dur);

    osc.connect(filter);
    filter.connect(env);
    env.connect(master);
    osc.start(now);
    osc.stop(now + dur + 0.05);
    activeNodesRef.current.push(osc);
    osc.onended = () => {
      activeNodesRef.current = activeNodesRef.current.filter((n) => n !== osc);
      try { osc.disconnect(); filter.disconnect(); env.disconnect(); } catch (err) { console.debug("breath-cue disconnect:", err); }
    };
  }, []);

  // Tear down on unmount
  useEffect(() => () => {
    activeNodesRef.current.forEach((n) => { try { n.stop(); } catch (err) { console.debug("breath-cue node stop:", err); } });
    activeNodesRef.current = [];
    if (ctxRef.current) {
      try { ctxRef.current.close(); } catch (err) { console.debug("breath-cue ctx close:", err); }
      ctxRef.current = null;
    }
  }, []);

  return { enabled, setEnabled, notify };
}
